import React, { createContext, useContext, useReducer, useEffect, useCallback, useRef } from 'react';
import { setAuthData, clearAuthData, getStoredUser, getStoredRole, getStoredToken } from '../services/api';

// ── Base64url → JSON decoder (JWT uses base64url, NOT standard base64) ─────
// atob() only handles standard base64 (+/). JWT uses base64url (-_).
// Without this fix: tokens with - or _ cause atob() to throw →
// catch returns null → isTokenExpired returns false → user appears logged out.
function decodeJWTPayload(token) {
  try {
    const base64url = token.split('.')[1];
    if (!base64url) return null;
    // Replace base64url chars → standard base64
    const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
    // Add = padding to make length multiple of 4
    const padded  = base64.padEnd(base64.length + (4 - base64.length % 4) % 4, '=');
    return JSON.parse(atob(padded));
  } catch {
    return null; // never throw — caller handles null gracefully
  }
}

// Only returns true when token is GENUINELY expired.
// If we can't decode → return false (benefit of doubt, let server decide).
function isTokenExpired(token) {
  if (!token) return true;
  const payload = decodeJWTPayload(token);
  if (!payload)     return false; // can't decode → treat as valid
  if (!payload.exp) return false; // no expiry set → always valid
  return payload.exp < Math.floor(Date.now() / 1000); // expired if past exp
}

// ── Reducer ────────────────────────────────────────────────────────────────
function authReducer(state, action) {
  switch (action.type) {
    case 'HYDRATED':
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user:              action.payload.user,
        role:              action.payload.role,
        token:             action.payload.token,
        isLoggedIn:        true,
        // Explicit check: true only if field exists AND is true
        isProfileComplete: action.payload.user?.isProfileComplete === true,
        isHydrated:        true,
      };
    case 'PROFILE_COMPLETE':
      return {
        ...state,
        user:              { ...state.user, ...action.payload, isProfileComplete: true },
        isProfileComplete: true,
      };
    case 'UPDATE_USER':
      return {
        ...state,
        user:              { ...state.user, ...action.payload },
        // Recalculate isProfileComplete in case it changed
        isProfileComplete: { ...state.user, ...action.payload }.isProfileComplete === true,
      };
    case 'LOGOUT':
      return {
        user: null, role: null, token: null,
        isLoggedIn: false, isProfileComplete: false,
        isHydrated: true,
      };
    case 'HYDRATION_DONE':
      return { ...state, isHydrated: true };
    default:
      return state;
  }
}

const initialState = {
  user: null, role: null, token: null,
  isLoggedIn: false, isProfileComplete: false,
  isHydrated: false,
};

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const logoutRef         = useRef(false); // prevent double-logout

  // ── Hydrate from localStorage on app load / page refresh ──────────────
  // Safety: if hydration takes > 3s (storage glitch), force-complete so
  // the app never hangs on a white loading screen.
  useEffect(() => {
    // Hard safety timeout — guarantees isHydrated becomes true within 3s
    // even if localStorage.getItem throws or blocks (quota, corruption, etc.)
    const safetyTimer = setTimeout(() => {
      dispatch({ type: 'HYDRATION_DONE' });
    }, 3000);

    try {
      const token = getStoredToken();
      const user  = getStoredUser();
      const role  = getStoredRole();

      if (token && user && role) {
        if (isTokenExpired(token)) {
          clearAuthData();
          dispatch({ type: 'HYDRATION_DONE' });
        } else {
          dispatch({ type: 'HYDRATED', payload: { token, user, role } });
        }
      } else {
        dispatch({ type: 'HYDRATION_DONE' });
      }
    } catch {
      clearAuthData();
      dispatch({ type: 'HYDRATION_DONE' });
    } finally {
      clearTimeout(safetyTimer); // hydration finished before timeout — cancel it
    }
  }, []); // runs EXACTLY ONCE on mount

  // ── Force logout from api.js 401 interceptor ──────────────────────────
  useEffect(() => {
    const handle = () => {
      if (logoutRef.current) return;
      logoutRef.current = true;
      clearAuthData();
      sessionStorage.removeItem('loginOrigin');
      sessionStorage.removeItem('pendingBooking');
      dispatch({ type: 'LOGOUT' });
      setTimeout(() => { logoutRef.current = false; }, 2000);
    };
    window.addEventListener('auth:logout', handle);
    return () => window.removeEventListener('auth:logout', handle);
  }, []);

  // ── Periodic expiry check every 10 minutes ────────────────────────────
  useEffect(() => {
    if (!state.isLoggedIn) return;
    const id = setInterval(() => {
      const t = getStoredToken();
      if (t && isTokenExpired(t)) {
        clearAuthData();
        dispatch({ type: 'LOGOUT' });
      }
    }, 10 * 60 * 1000);
    return () => clearInterval(id);
  }, [state.isLoggedIn]);

  // ── loginSuccess ───────────────────────────────────────────────────────
  // Called right after a successful login API response.
  // Saves token + user + role to localStorage so refresh keeps user logged in.
  const loginSuccess = useCallback((token, user, role) => {
    setAuthData(token, user, role); // ← writes to localStorage
    dispatch({ type: 'LOGIN_SUCCESS', payload: { token, user, role } });
    window.dispatchEvent(new Event('auth:login'));
  }, []);

  // ── profileComplete ────────────────────────────────────────────────────
  // Called after patient fills name/age/gender during onboarding.
  const profileComplete = useCallback((userData) => {
    // Read current stored user to avoid stale closure issue
    const currentUser = getStoredUser();
    const updated = { ...currentUser, ...userData, isProfileComplete: true };
    const currentToken = getStoredToken();
    const currentRole  = getStoredRole();
    setAuthData(currentToken, updated, currentRole); // persist to localStorage
    dispatch({ type: 'PROFILE_COMPLETE', payload: userData });
  }, []);

  // ── updateUser ─────────────────────────────────────────────────────────
  const updateUser = useCallback((userData) => {
    const currentUser  = getStoredUser();
    const currentToken = getStoredToken();
    const currentRole  = getStoredRole();
    const updated      = { ...currentUser, ...userData };
    setAuthData(currentToken, updated, currentRole);
    dispatch({ type: 'UPDATE_USER', payload: userData });
  }, []);

  // ── logout ─────────────────────────────────────────────────────────────
  // Clears ALL stored auth data. Caller is responsible for navigation.
  const logout = useCallback(() => {
    if (logoutRef.current) return;
    logoutRef.current = true;
    clearAuthData();
    sessionStorage.removeItem('loginOrigin');
    sessionStorage.removeItem('pendingBooking');
    dispatch({ type: 'LOGOUT' });
    // Don't emit auth:logout here — that causes the listener above to run too
    // Just let navigation happen in the calling component
    setTimeout(() => { logoutRef.current = false; }, 2000);
  }, []);

  return (
    <AuthContext.Provider value={{
      ...state,
      loginSuccess,
      profileComplete,
      updateUser,
      logout,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
